import { SetStateAction, useState } from 'react';

import {
  carrierColumn,
  checkboxColumn,
  marketColumn,
  orderDateColumn,
  orderNoColumn,
  productNameColumn,
  productNoColumn,
  productOptionColumn,
  quantityColumn,
  receiverAddressColumn,
  receiverColumn,
  recevierMemo,
  salesAmountColumn,
  trackingNumberColumn,
} from '../orderTableColumnBlocks';

import EmptyFaceIcon from '@/assets/icons/EmptyFaceIcon.svg?react';
import { TooltipProvider } from '@/components/atoms/PcTooltip';
import { DEFAULT_PAGE_SIZE } from '@/constants/order';
import { useShippingCompanyPreference } from '@/pages/orders/_hooks/useShippingCompanyPreference';
import { LayoutOrderColumn } from '@/pages/orders/_layouts/LayoutOrderColumn';
import { isSameOrderItem } from '@/pages/orders/_utils/isSameOrderItem';
import { OrderItem } from '@/types/order-list';
import { CarrierState } from '@/types/shipping';
import MarketTable from '../../../common/MarketTable';
import PaginationSection from '../../../common/PaginationSection';
import { useOrderDetails } from '../../OrderDetailsProvider';
import PleaseFullfillModal from './PleaseFullfillModal';

interface Props {
  page: number;
  onChangePage: (page: number) => void;
  totalPages: number;
  orderList: OrderItem[];
}

export function OrderTableColumn({ page, onChangePage, totalPages, orderList }: Props) {
  const {
    checkedOrders,
    setCheckedOrders,
    shippingCompanyData,
    carrierState,
    setCarrierState,
    errorCarrierState,
    setErrorCarrierState,
  } = useOrderDetails();

  const { handlePrefer } = useShippingCompanyPreference(shippingCompanyData);

  const hasCarrierInfo = (order: OrderItem, shippingInfo: CarrierState[]) => {
    const info = shippingInfo.find((item) => isSameOrderItem(item.target, order));
    // carrier와 trackingNumber가 모두 한 글자라도 있으면 true
    if (!info) return false;
    const carrierHasValue =
      info.carrier && typeof info.carrier === 'object' && Object.keys(info.carrier).length > 0;
    const trackingHasValue =
      typeof info.trackingNumber === 'string' && info.trackingNumber.length > 0;
    return carrierHasValue && trackingHasValue;
  };

  const handleCheck = (order: OrderItem, checked: boolean) => {
    if (checked && !hasCarrierInfo(order, carrierState)) {
      setFullfillModalOpen(true);
      return;
    }
    setCheckedOrders((prev) =>
      checked ? [...prev, order] : prev.filter((item) => !isSameOrderItem(item, order)),
    );
  };

  const selectableOrders = orderList.filter((order) => order.marketInfo.isOrderFurtherActionEnable);
  const allChecked =
    selectableOrders.length > 0 && checkedOrders.length === selectableOrders.length;

  const handleCheckAll = (checked: boolean) => {
    if (selectableOrders.length === 0) return; // 선택 가능한 주문이 없으면 아무 작업도 하지 않음

    if (checked) {
      // 체크하려는 모든 order가 carrierState에 운송정보가 있어야 함
      const allFulfilled = selectableOrders.every((order) => hasCarrierInfo(order, carrierState));
      if (!allFulfilled) {
        setFullfillModalOpen(true);
        return;
      }
      setCheckedOrders(selectableOrders);
    } else {
      setCheckedOrders([]);
    }
  };

  // OrderTableColumn 내부
  const handleSetCarrierState = (updater: SetStateAction<CarrierState[]>) => {
    setCarrierState((prevCarrierState) => {
      const nextCarrierState = typeof updater === 'function' ? updater(prevCarrierState) : updater;

      // fulfilled 조건
      const fulfilledOrders = nextCarrierState
        .filter(
          (item: CarrierState) =>
            item.carrier &&
            typeof item.carrier === 'object' &&
            Object.keys(item.carrier).length > 0 &&
            typeof item.trackingNumber === 'string' &&
            item.trackingNumber.length > 0,
        )
        .map((item: CarrierState) => item.target);

      // checkedOrders 동기화(추가/제거 모두)
      setCheckedOrders((prevChecked) =>
        prevChecked
          .filter((order) =>
            fulfilledOrders.some((fulfilled) => isSameOrderItem(fulfilled, order)),
          )
          .concat(
            fulfilledOrders.filter(
              (order) => !prevChecked.some((checkedOrder) => isSameOrderItem(checkedOrder, order)),
            ),
          ),
      );

      return nextCarrierState;
    });
  };

  // columns는 항상 'checkbox'가 맨 앞에 오도록
  const columns = [
    ...(selectableOrders.length > 0
      ? [checkboxColumn({ allChecked, handleCheckAll, checkedOrders, handleCheck }), orderNoColumn]
      : []),
    marketColumn,
    orderDateColumn,
    productNameColumn,
    productOptionColumn,
    quantityColumn,
    ...(shippingCompanyData && shippingCompanyData.length > 0
      ? [
          carrierColumn(shippingCompanyData, handlePrefer, carrierState, handleSetCarrierState),
          trackingNumberColumn(
            carrierState,
            handleSetCarrierState,
            errorCarrierState,
            setErrorCarrierState,
          ),
        ]
      : []),
    productNoColumn(),
    receiverColumn,
    receiverAddressColumn,
    recevierMemo,
    salesAmountColumn,
  ];

  // columnOrder는 'checkbox'를 제외한 나머지 컬럼 id만 포함
  const [columnOrder, setColumnOrder] = useState(() =>
    columns.filter((c) => c.id !== 'checkbox').map((c) => c.id!),
  );
  const [fullfillModalOpen, setFullfillModalOpen] = useState(false);
  return (
    <TooltipProvider>
      <LayoutOrderColumn className="relative">
        {/* MarketTable을 감싸는 div 추가 */}
        <div className="min-h-0 overflow-auto">
          {/* ✅ 남은 공간 모두 차지 + 스크롤 */}
          <MarketTable
            orderList={orderList}
            columns={columns}
            columnOrder={columnOrder}
            setColumnOrder={setColumnOrder}
            checkedOrders={checkedOrders}
            setCheckedOrders={setCheckedOrders}
            pageIndex={page}
            pageSize={DEFAULT_PAGE_SIZE}
            pageCount={Math.ceil(orderList.length / DEFAULT_PAGE_SIZE)} // 클라이언트사이드 시
          />
          {orderList.length === 0 && (
            <div className="absolute inset-0 top-[40px] z-[20] flex flex-col items-center justify-center gap-[20px] rounded-[20px] bg-white">
              <EmptyFaceIcon />
              <p className="text-gr-500">검색결과가 없어요.</p>
            </div>
          )}
        </div>

        {/* 페이지네이션 컨트롤 */}
        <div className="flex h-[70px] max-h-[70px] items-center justify-center gap-4 border-t">
          <PaginationSection page={page} onChangePage={onChangePage} totalPages={totalPages} />
        </div>
        <PleaseFullfillModal open={fullfillModalOpen} onOpenChange={setFullfillModalOpen} />
      </LayoutOrderColumn>
    </TooltipProvider>
  );
}
